import { useMemo, useState } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Check, Copy, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import { Conversation } from "@/types/conversation/conversation";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface ConversationSuggestedFollowUpProps {
  conversation: Conversation;
}

type DraftKind = "sms" | "email";

/**
 * Pre-filled follow-up draft (SMS + email) the owner can send or copy.
 * When the call captured the customer's email/phone, the primary action
 * opens the owner's mail/SMS client pre-addressed with the draft — the
 * "continue the conversation" affordance. Copy remains as fallback.
 * Pure interpolation from the conversation's lead_info + topics — no AI
 * in phase 1. The coaching engine upgrades this body when it ships.
 *
 * Brand chrome per guidelines/design.md §6.
 */
export function ConversationSuggestedFollowUp({ conversation }: ConversationSuggestedFollowUpProps) {
  const [kind, setKind] = useState<DraftKind>("email");
  const [copied, setCopied] = useState(false);

  const drafts = useMemo(() => buildDrafts(conversation), [conversation]);
  const active = drafts[kind];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(active.body);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Select and copy manually.");
    }
  };

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm">
      <div className="rounded-[14px] bg-white overflow-hidden">
        <div
          className="px-5 md:px-6 py-4 border-b border-[#4E9CFF]/10"
          style={{
            background: "linear-gradient(135deg, rgba(78,156,255,0.06) 0%, rgba(255,77,184,0.06) 100%)",
          }}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <GenieIcon
                name="mail"
                size={26}
                color="#4E9CFF"
                secondaryColor="#FF4DB8"
                gradientEnabled
                label="Follow-up draft"
              />
              <h3 className="text-base font-bold text-gray-900">Suggested follow-up</h3>
            </div>
            <div className="p-[1.5px] rounded-lg bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
              <div className="flex rounded-[6px] bg-white">
                <button
                  onClick={() => setKind("email")}
                  className={`px-3 py-1.5 rounded-l-[6px] text-xs font-semibold transition-colors ${
                    kind === "email" ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  Email
                </button>
                <button
                  onClick={() => setKind("sms")}
                  className={`px-3 py-1.5 rounded-r-[6px] text-xs font-semibold transition-colors ${
                    kind === "sms" ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  SMS
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 md:px-6 py-5">
          {active.subject && (
            <div className="mb-3">
              <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1">Subject</div>
              <div className="text-sm font-medium text-gray-900">{active.subject}</div>
            </div>
          )}
          <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
            {kind === "sms" ? "Message" : "Body"}
          </div>
          <pre
            className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed p-4 rounded-lg border border-gray-200"
            style={{ background: "#FAFBFC" }}
          >
            {active.body}
          </pre>
          {active.missingNote && <div className="text-[11px] text-gray-500 mt-2">{active.missingNote}</div>}
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-gray-500">
              {active.to
                ? kind === "sms"
                  ? `Opens a text to ${active.to} in your messaging app.`
                  : <>Opens an email to <CopyableEmail email={active.to} /> in your mail client.</>
                : kind === "sms"
                  ? "Paste into your SMS or messaging app."
                  : "Paste into your email client. Adjust before sending."}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleCopy}
                className={
                  active.to
                    ? "inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors"
                    : "inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white text-sm font-semibold shadow-md hover:opacity-90 hover:scale-[1.02] transition-all"
                }
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Copied" : `Copy ${kind === "sms" ? "SMS" : "email"}`}
              </button>
              {active.to && (
                <a
                  href={buildSendHref(kind, active)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white text-sm font-semibold shadow-md hover:opacity-90 hover:scale-[1.02] transition-all"
                >
                  {kind === "sms" ? <MessageSquare className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                  {kind === "sms" ? "Text them back" : "Email them back"}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface Draft {
  subject?: string;
  body: string;
  missingNote?: string;
  /** Captured customer email (for email drafts) or phone (for SMS drafts). */
  to?: string | null;
}

function buildSendHref(kind: DraftKind, draft: Draft): string {
  if (kind === "sms") {
    return `sms:${draft.to}?body=${encodeURIComponent(draft.body)}`;
  }
  const subject = draft.subject ? `subject=${encodeURIComponent(draft.subject)}&` : "";
  return `mailto:${draft.to}?${subject}body=${encodeURIComponent(draft.body)}`;
}

function buildDrafts(conversation: Conversation): Record<DraftKind, Draft> {
  const leadInfo = conversation.details?.lead_info || {};
  const customerData =
    (conversation.details?.analysis_result?.customer_data as Record<string, unknown> | undefined) ||
    (conversation.metadata?.customer_data as Record<string, unknown> | undefined) ||
    {};
  const topics: string[] = Array.isArray(conversation.details?.topics) ? conversation.details?.topics : [];
  const summary: string = typeof conversation.details?.summary === "string" ? conversation.details.summary : "";

  const callerName =
    pickString(leadInfo.full_name) ||
    pickString(leadInfo.name) ||
    pickString(leadInfo.first_name) ||
    pickString(customerData.name) ||
    "there";
  const firstName = callerName.split(" ")[0] || callerName;

  const callerEmail = pickString(leadInfo.email) || pickString(customerData.email);
  const callerPhone =
    pickString(leadInfo.phone) ||
    pickString(leadInfo.phone_number) ||
    pickString(customerData.phone);

  const genieName: string = conversation.agent?.name || "our team";
  const primaryTopic = topics[0] || null;

  const topicLine = primaryTopic ? `about ${primaryTopic.toLowerCase()}` : "today";

  const summarySentence = summary
    ? `Just to recap: ${trimSentence(summary, 220)}`
    : "Wanted to follow up and see if there's anything else we can help with.";

  const missingBits: string[] = [];
  if (callerName === "there") missingBits.push("name");
  if (!callerEmail && !callerPhone) missingBits.push("contact details");

  const missingNote =
    missingBits.length > 0
      ? `Caller's ${missingBits.join(" and ")} not captured. Update insights so future calls fill these in automatically.`
      : undefined;

  const emailBody = [
    `Hi ${firstName},`,
    ``,
    `Thanks for the call with ${genieName} ${topicLine}.`,
    ``,
    summarySentence,
    ``,
    `If you've got any other questions, just reply to this email${
      callerPhone ? ` or give us a call back` : ""
    }. Happy to help.`,
    ``,
    `Thanks,`,
    `The team`,
  ].join("\n");

  const smsBody = primaryTopic
    ? `Hi ${firstName}, thanks for the call about ${primaryTopic.toLowerCase()}. If you'd like to pick up where we left off, just reply or call back. — the team`
    : `Hi ${firstName}, thanks for the call today. If you'd like to pick up where we left off, just reply or call back. — the team`;

  return {
    email: {
      subject: primaryTopic
        ? `Following up on your call about ${primaryTopic.toLowerCase()}`
        : `Following up on your call`,
      body: emailBody,
      missingNote,
      to: callerEmail,
    },
    sms: {
      body: smsBody,
      missingNote,
      to: callerPhone,
    },
  };
}

function pickString(v: unknown): string | null {
  if (typeof v === "string" && v.trim().length > 0) return v.trim();
  return null;
}

function trimSentence(s: string, max: number): string {
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd() + "…";
}
